<template>
  <ai-popin v-model="opened" overlay-listener @overlay-click="onRefresh">
    <ai-popin-card
      :description="$t('booking.popin.priceStale.description')"
      :title="$t('booking.popin.priceStale.title')"
      @close="opened = false">
      <template #actions>
        <div class="BookingRefreshPrice-actions">
          <div class="BookingRefreshPrice-refreshPrice">
            <ai-button
              :label="$t('booking.popin.priceStale.refresh')"
              @click="onRefresh" />
          </div>
          <div>
            <ai-button
              :label="$t('booking.popin.priceStale.redirect')"
              :url="redirectUrl"
              as-link
              variant="tertiary" />
          </div>
        </div>
      </template>
    </ai-popin-card>
  </ai-popin>
</template>

<script lang="ts" setup>
import { useBookingPriceStaleTimer, useBookingStore } from '~/domains/booking';
import { useHotelQuery } from '~/domains/graphql';
import { formatDate } from '~/helpers/date.helper';

const localePath = useLocalePath();
const { stalePriceTimerReached, onShouldRedirect } =
  useBookingPriceStaleTimer();
const opened = ref(false);

watch(stalePriceTimerReached, reached => {
  if (!reached) return;

  opened.value = true;
});

const onRefresh = () => onShouldRedirect({ pre: () => (opened.value = false) });

const bookingStore = useBookingStore();

const { result } = useHotelQuery({ hotelId: bookingStore.hotelId as string });
const hotelSlug = computed(() => result.value?.hotel?.slug);
const redirectUrl = computed(() => {
  const rawFirstDate = new Date(bookingStore.isoDateIn as string);
  const rawLastDate = new Date(bookingStore.isoDateOut as string);

  const dates = [rawFirstDate, rawLastDate];

  const roomsAndGuests = [
    ...bookingStore.rooms,
    ...bookingStore.remainingGuests,
  ].reduce<{ adults: number; childrenAges: number[] }[]>(
    (accumulator, room) => {
      accumulator.push({
        adults: room.adults,
        childrenAges: room.childrenAges,
      });

      return accumulator;
    },
    [],
  );

  return localePath({
    name: 'hotel-hotelSlug',
    params: {
      hotelSlug: hotelSlug.value as string,
    },
    query: {
      dates: JSON.stringify(dates.map(date => formatDate(date, 'yyyy-MM-dd'))),
      roomsAndGuests: JSON.stringify(roomsAndGuests),
    },
  });
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';

.BookingRefreshPrice-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.BookingRefreshPrice-refreshPrice {
  margin-bottom: constants.$margin-02;
}
</style>
